import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
const emptyStr = (v) => (v === '' || v == null ? null : v);

const defaultForm = {
  vendorCode: '',
  vendorName: '',
  taxId: '',
  paymentTerms: '',
  paymentMethod: 'ACH',
  contactNumber: '',
  address: '',
  emailId: '',
  status: 'ACTIVE',
  isTrusted: false,
};

const mapVendorRow = (item) => ({
  id: item.vendorId,
  vendorCode: item.vendorCode,
  vendorName: item.vendorName,
  taxId: item.taxId,
  paymentTerms: item.paymentTerms,
  paymentMethod: item.paymentMethod,
  contactNumber: item.contactNumber,
  emailId: item.emailId,
  address: item.address,
  status: item.status,
  isTrusted: Boolean(item.isTrusted),
  raw: item,
});

const VendorMaster = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.VENDOR_MASTER);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [focusedField, setFocusedField] = useState('');

  const focusLabelProps = (fieldName, hasValue) => {
    const active = focusedField === fieldName;
    return {
      shrink: hasValue || active,
      sx: (hasValue || active) ? { bgcolor: 'background.paper', px: 0.5 } : undefined,
    };
  };

  const fieldFocus = (fieldName) => ({
    onFocus: () => setFocusedField(fieldName),
    onBlur: () => setFocusedField((current) => (current === fieldName ? '' : current)),
  });

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchVendors = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/vendor-masters/tenant/${tenantId}`);
      setRows((Array.isArray(data) ? data : []).map(mapVendorRow));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const columns = useMemo(() => [
    { field: 'vendorCode', headerName: 'Vendor Code', width: 140, resizable: true },
    { field: 'vendorName', headerName: 'Vendor Name', width: 220, resizable: true },
    { field: 'taxId', headerName: 'Tax ID', width: 140, resizable: true },
    { field: 'paymentTerms', headerName: 'Payment Terms', width: 150, resizable: true },
    { field: 'paymentMethod', headerName: 'Payment Method', width: 140, resizable: true },
    { field: 'contactNumber', headerName: 'Contact Number', width: 150, resizable: true },
    { field: 'emailId', headerName: 'Email ID', width: 200, resizable: true },
    { field: 'address', headerName: 'Address', width: 220, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
    {
      field: 'isTrusted',
      headerName: 'Trusted',
      width: 100,
      resizable: true,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
  ], []);

  const buildPayload = () => ({
    tenantId,
    vendorCode: form.vendorCode?.trim(),
    vendorName: form.vendorName?.trim(),
    taxId: emptyStr(form.taxId),
    paymentTerms: emptyStr(form.paymentTerms),
    paymentMethod: emptyStr(form.paymentMethod),
    contactNumber: emptyStr(form.contactNumber?.trim()),
    emailId: emptyStr(form.emailId?.trim()),
    address: emptyStr(form.address?.trim()),
    status: form.status || 'ACTIVE',
    isTrusted: Boolean(form.isTrusted),
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setFocusedField('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setFocusedField('');
    setForm({
      vendorCode: selectedRow.vendorCode || '',
      vendorName: selectedRow.vendorName || '',
      taxId: selectedRow.taxId || '',
      paymentTerms: selectedRow.paymentTerms || '',
      paymentMethod: selectedRow.paymentMethod || 'ACH',
      contactNumber: selectedRow.contactNumber || '',
      address: selectedRow.address || '',
      emailId: selectedRow.emailId || '',
      status: selectedRow.status || 'ACTIVE',
      isTrusted: Boolean(selectedRow.isTrusted),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/vendor-masters', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('Vendor Master saved successfully');
      } else {
        await apiFetch(`conductor/vendor-masters/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('Vendor Master updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchVendors();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/vendor-masters/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchVendors();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Vendor Master</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Vendor Master' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add Vendor Master' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete Vendor Master' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Vendor Master' : 'Edit Vendor Master'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="Vendor Code" value={form.vendorCode} onChange={(e) => setForm((p) => ({ ...p, vendorCode: e.target.value }))} InputLabelProps={focusLabelProps('vendorCode', Boolean(form.vendorCode))} {...fieldFocus('vendorCode')} />
            <TextField fullWidth size="small" required label="Vendor Name" value={form.vendorName} onChange={(e) => setForm((p) => ({ ...p, vendorName: e.target.value }))} InputLabelProps={focusLabelProps('vendorName', Boolean(form.vendorName))} {...fieldFocus('vendorName')} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" label="Tax ID" value={form.taxId} onChange={(e) => setForm((p) => ({ ...p, taxId: e.target.value }))} InputLabelProps={focusLabelProps('taxId', Boolean(form.taxId))} {...fieldFocus('taxId')} />
            <TextField fullWidth size="small" label="Payment Terms" value={form.paymentTerms} onChange={(e) => setForm((p) => ({ ...p, paymentTerms: e.target.value }))} InputLabelProps={focusLabelProps('paymentTerms', Boolean(form.paymentTerms))} {...fieldFocus('paymentTerms')} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" select label="Payment Method" value={form.paymentMethod} onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))} InputLabelProps={focusLabelProps('paymentMethod', Boolean(form.paymentMethod))} {...fieldFocus('paymentMethod')} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACH">ACH</MenuItem>
              <MenuItem value="WIRE">WIRE</MenuItem>
              <MenuItem value="CHECK">CHECK</MenuItem>
            </TextField>
            <TextField fullWidth size="small" label="Contact Number" value={form.contactNumber} onChange={(e) => setForm((p) => ({ ...p, contactNumber: e.target.value }))} InputLabelProps={focusLabelProps('contactNumber', Boolean(form.contactNumber))} {...fieldFocus('contactNumber')} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" type="email" label="Email ID" value={form.emailId} onChange={(e) => setForm((p) => ({ ...p, emailId: e.target.value }))} InputLabelProps={focusLabelProps('emailId', Boolean(form.emailId))} {...fieldFocus('emailId')} />
            <TextField fullWidth size="small" select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} InputLabelProps={focusLabelProps('status', Boolean(form.status))} {...fieldFocus('status')} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" multiline rows={3} label="Address" value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} InputLabelProps={focusLabelProps('address', Boolean(form.address))} {...fieldFocus('address')} />
          </Box>
          {canEdit || (mode === 'add' && canAdd) ? (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={Boolean(form.isTrusted)}
                  onChange={(e) => setForm((p) => ({ ...p, isTrusted: e.target.checked }))}
                  disabled={mode === 'add' ? !canAdd : !canEdit}
                />
              )}
              label="Trusted vendor (auto-approve draft invoices when GL coding and validations pass)"
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}><Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{rowPendingDelete?.vendorName || 'selected'}&quot;?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default VendorMaster;
